import { ClientItemId } from 'owa-client-ids';
import { isFeatureEnabled } from 'owa-feature-flags';
import { orchestrator } from 'satcheljs';
import { tryQueueGetWacInfo } from '../actions/internalActions';
import { addOpenDocumentPreviewEventListener } from '../actions/internalActions';
import { previewLinkInSxS } from '../actions/publicActions';
import {
    ORIGINALSRC_ATTTIBUTE_NAME,
    SP_LINK_CLICK_EVENT_LISTENER,
    SP_LINK_MOUSEUP_EVENT_LISTENER,
} from '../utils/constants';
import documentLinkMouseUpHandler from '../utils/documentLinkMouseUpHandler';
import forceGetWacInfoOperation from '../utils/forceGetWacInfoOperation';
import { getValidDocLinkPreviewInfoWithReason } from '../utils/getValidDocLinkPreviewInfo';
import isSafeLink from '../utils/isSafeLink';
import logSharePointLinkClick from '../utils/logSharePointLinkClick';
import serviceGeneratedLinkClickHandler from '../utils/serviceGeneratedLinkClickHandler';

orchestrator(addOpenDocumentPreviewEventListener, actionMessage => {
    const { element, href, itemId, isSafeLinkWrapped, isSafeLinkVerified } = actionMessage;
    tryQueueGetWacInfo(href);
    const eventListener = (ev: MouseEvent) => {
        documentLinkPreviewClickHandler(ev, itemId, isSafeLinkWrapped, isSafeLinkVerified);
    };
    element.addEventListener('click', eventListener);
    element[SP_LINK_CLICK_EVENT_LISTENER] = eventListener;

    const mouseupListener = (ev: MouseEvent) => {
        documentLinkMouseUpHandler(ev, isSafeLinkWrapped, isSafeLinkVerified);
    };
    element.addEventListener('mouseup', mouseupListener);
    element[SP_LINK_MOUSEUP_EVENT_LISTENER] = mouseupListener;
});

function documentLinkPreviewClickHandler(
    ev: MouseEvent,
    itemId: ClientItemId,
    isSafeLinkWrapped: boolean,
    isSafeLinkVerified: boolean
) {
    let newTabReason: string = null;

    // only try to open in SxS if non of the modifier keys is pressed
    if (!ev.altKey && !ev.ctrlKey && !ev.metaKey && !ev.shiftKey) {
        const anchorElement = <HTMLAnchorElement>ev.currentTarget;

        // For JsAPI, we don't need evaluate SafeLink as it will be handled on SharePoint side
        let url: string = null;
        if (isFeatureEnabled('doc-SxS-jsApi-ODB-Links') && isSafeLink(anchorElement)) {
            url = anchorElement.getAttribute(ORIGINALSRC_ATTTIBUTE_NAME);
        } else {
            url = anchorElement.href;
        }

        const { docLinkPreviewInfo, reason } = getValidDocLinkPreviewInfoWithReason(url);
        if (docLinkPreviewInfo?.needRedeem || reason === 'Expired') {
            previewLinkInSxS(async () => {
                const docLinkPreviewInfo2 = await forceGetWacInfoOperation(
                    url,
                    true /* shouldReturnDocLinkPreviewInfo */,
                    true /* redeemSharingLinkIfNecessary */
                );

                const extraInfo = docLinkPreviewInfo?.needRedeem
                    ? 'redeem'
                    : 'retryAfterCacheExpire';
                logSharePointLinkClick(null, isSafeLinkWrapped, isSafeLinkVerified, extraInfo);
                return { ...docLinkPreviewInfo2.wacUrlInfo, extraInfo };
            }, itemId);
            ev.preventDefault();
            return;
        }

        if (!!docLinkPreviewInfo) {
            previewLinkInSxS(() => Promise.resolve(docLinkPreviewInfo.wacUrlInfo), itemId);
            ev.preventDefault();
            logSharePointLinkClick(null, isSafeLinkWrapped, isSafeLinkVerified);
            return;
        }

        newTabReason = reason;
    }

    // If the code reached this point
    // the browser will open a new tab of the link
    serviceGeneratedLinkClickHandler(ev, isSafeLinkWrapped, isSafeLinkVerified, newTabReason);
}
